import { useState, useEffect } from 'react';
import { Link, Outlet, NavLink, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { billingApi } from '~/lib/api';
import { useAuth } from '~/contexts/AuthContext';
import { WebSocketProvider, useWebSocketContext } from '~/contexts/WebSocketContext';
import { useInstallPrompt } from '~/hooks/useInstallPrompt';
import { usePushNavigation } from '~/hooks/usePushNavigation';

export function Layout() {
  // Gate the WebSocket on the user being fetched so PWAs that pick up the
  // session via cookie (and have to wait for /api/auth/me to mirror the JWT
  // into localStorage) reconnect once the token is available, instead of
  // failing the first connect attempt and sitting in "Not authenticated".
  const { user } = useAuth();
  return (
    <WebSocketProvider enabled={!!user}>
      <LayoutInner />
    </WebSocketProvider>
  );
}

function LayoutInner() {
  const { user, isAdmin, logout } = useAuth();
  const { connected, reconnecting, error: wsError } = useWebSocketContext();
  const { canInstall, install, showIOSGuide, dismissIOSGuide } = useInstallPrompt();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  usePushNavigation();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Billing status for subscribe button
  const { data: billingData } = useQuery({
    queryKey: ['billing'],
    queryFn: billingApi.status,
  });
  const hasActiveSubscription = billingData?.isActive ?? false;

  const checkoutMutation = useMutation({
    mutationFn: billingApi.createCheckout,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  return (
    <div className="min-h-screen app-bg text-text-primary">
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <header className="glass-nav sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-12 sm:h-16">
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
                <Link
                  to="/"
                  className="flex items-center gap-2 min-w-0 group"
                >
                  <img
                    src="/eagle.png"
                    alt="King's Tracker"
                    width={512}
                    height={512}
                    className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 object-contain"
                  />
                  <h1 className="font-display text-lg sm:text-2xl font-bold tracking-wide text-gilded whitespace-nowrap leading-none">
                    King's Tracker
                  </h1>
                </Link>
                {/* WebSocket live indicator. Tapping a disconnected badge
                    surfaces the underlying error (mobile users have no devtools
                    so this is the only way to debug stuck-red states). */}
                <button
                  type="button"
                  onClick={() => {
                    if (connected) return;
                    const wsUrl = (import.meta.env.VITE_API_URL || '')
                      .replace(/^https?:\/\//, 'wss://') + '/api/ws';
                    const lines = [
                      reconnecting ? 'Status: reconnecting' : 'Status: disconnected',
                      wsError ? `Error: ${wsError}` : 'Error: (none reported)',
                      `WS URL: ${wsUrl}`,
                      `User agent: ${navigator.userAgent}`,
                      `Standalone PWA: ${window.matchMedia('(display-mode: standalone)').matches}`,
                    ];
                    alert(lines.join('\n\n'));
                  }}
                  className={`w-2 h-2 rounded-full shrink-0 cursor-pointer translate-y-[3px] sm:translate-y-[4px] ${
                    connected
                      ? 'bg-accent pulse-live'
                      : reconnecting
                      ? 'bg-warning animate-pulse'
                      : 'bg-loss'
                  }`}
                  title={
                    connected
                      ? 'Live — connected'
                      : reconnecting
                      ? 'Reconnecting... (tap for details)'
                      : 'Disconnected (tap for details)'
                  }
                  aria-label="WebSocket connection status"
                />
              </div>

              <div className="flex items-center">
                {/* Desktop nav links */}
                <nav className="hidden sm:flex items-center space-x-1 mr-2">
                  <HeaderNavItem to="/">Plays</HeaderNavItem>
                  <HeaderNavItem to="/traders">Traders</HeaderNavItem>
                  <HeaderNavItem to="/alerts">Alerts</HeaderNavItem>
                </nav>

                {/* Install button (mobile only) */}
                {canInstall && (
                  <button
                    onClick={install}
                    className="sm:hidden flex items-center gap-1 px-2 py-1 text-text-secondary hover:text-text-primary transition-colors text-sm"
                    aria-label="Install app"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                    </svg>
                    Install
                  </button>
                )}

                {/* Hamburger button */}
                <button
                  className="p-2 text-text-secondary hover:text-text-primary transition-colors"
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label="Toggle menu"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </header>

        {/* Slide-in drawer + backdrop */}
        <div
          className={`fixed inset-0 z-40 transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={!menuOpen}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />

          {/* Drawer panel */}
          <nav
            className={`absolute top-0 right-0 h-full w-64 bg-surface-1 border-l border-border flex flex-col transition-transform duration-300 ease-out ${
              menuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Close button */}
            <div className="flex items-center justify-between px-4 h-12 sm:h-16 border-b border-border">
              <span className="text-sm font-medium text-text-secondary">Menu</span>
              <button
                className="p-1 text-text-secondary hover:text-text-primary transition-colors"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              <DrawerNavItem to="/">Plays</DrawerNavItem>
              <DrawerNavItem to="/traders">Traders</DrawerNavItem>
              <DrawerNavItem to="/alerts">Alerts</DrawerNavItem>
              {isAdmin && <DrawerNavItem to="/debug">Debug</DrawerNavItem>}
              {isAdmin && <DrawerNavItem to="/settings/users">Users</DrawerNavItem>}
              <DrawerNavItem to="/settings">Settings</DrawerNavItem>

              {!hasActiveSubscription && (
                <>
                  <div className="border-t border-border my-3" />
                  <button
                    onClick={() => {
                      checkoutMutation.mutate();
                      setMenuOpen(false);
                    }}
                    disabled={checkoutMutation.isPending}
                    className="w-full text-left px-3 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 text-sm font-semibold rounded-lg"
                  >
                    {checkoutMutation.isPending ? '...' : 'Upgrade'}
                  </button>
                </>
              )}

              {canInstall && (
                <>
                  <div className="border-t border-border my-3" />
                  <button
                    onClick={() => {
                      install();
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-surface-3 hover:bg-surface-3/80 text-text-primary text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4" />
                    </svg>
                    Install App
                  </button>
                </>
              )}
            </div>

            {/* User footer */}
            <div className="border-t border-border px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 min-w-0">
                  {user?.avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=32`}
                      alt={user.username}
                      className="w-6 h-6 rounded-full shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-surface-3 flex items-center justify-center text-xs shrink-0">
                      {user?.username?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm text-text-secondary truncate">{user?.username}</span>
                </div>
                <button
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                  className="text-xs text-text-muted hover:text-text-primary transition-colors shrink-0 ml-2"
                >
                  Logout
                </button>
              </div>
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex flex-col max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 sm:pb-6">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="bg-surface-1 border-t border-border py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-text-muted text-sm">
            <span className="font-display tracking-wide text-text-secondary">King's Tracker</span> · v0.1.0
          </div>
        </footer>
      </div>

      {/* Floating bottom nav — mobile only. Pill-shaped with real
          frosted-glass backdrop (translucent + backdrop-blur), centred
          horizontally, hugs the bottom safe-area. The top header stays
          too; this is the fast-reach option on phones. */}
      <nav
        className="
          sm:hidden fixed left-1/2 -translate-x-1/2 z-40
          bottom-[calc(env(safe-area-inset-bottom,0px)+12px)]
          flex items-center gap-1 px-2 py-1.5
          rounded-full
          bg-white/5 backdrop-blur-xl backdrop-saturate-150
          border border-white/10
          shadow-[0_10px_30px_-8px_rgba(0,0,0,0.7)]
        "
        aria-label="Bottom navigation"
      >
        <FloatingNavItem to="/" end>Plays</FloatingNavItem>
        <FloatingNavItem to="/traders">Traders</FloatingNavItem>
        <FloatingNavItem to="/alerts">Alerts</FloatingNavItem>
      </nav>

      {/* iOS Install Guide */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={dismissIOSGuide} />
          <div className="relative bg-surface-2 border border-border rounded-2xl p-5 max-w-sm w-full mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-primary">Install App</h3>
              <button
                onClick={dismissIOSGuide}
                className="p-1 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ol className="space-y-3 text-sm text-text-secondary">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-surface-3 flex items-center justify-center text-xs font-medium text-text-primary">1</span>
                <span>
                  Tap the <strong className="text-text-primary">Share</strong> button
                  <svg className="inline w-4 h-4 ml-1 -mt-0.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0-12l-4 4m4-4l4 4M4 14v5a2 2 0 002 2h12a2 2 0 002-2v-5" />
                  </svg>
                  {' '}in the toolbar
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-surface-3 flex items-center justify-center text-xs font-medium text-text-primary">2</span>
                <span>Scroll down and tap <strong className="text-text-primary">Add to Home Screen</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-surface-3 flex items-center justify-center text-xs font-medium text-text-primary">3</span>
                <span>Tap <strong className="text-text-primary">Add</strong> to confirm</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderNavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }: { isActive: boolean }) =>
        `px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
          isActive
            ? 'bg-surface-3 text-text-primary'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-2'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function DrawerNavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }: { isActive: boolean }) =>
        `block px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
          isActive
            ? 'bg-surface-3 text-text-primary'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-2'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

// Pill-shaped item used inside the floating bottom nav. The active pill
// uses a translucent accent overlay (rather than a solid fill) so the
// frosted glass + content blur stays visible through it.
function FloatingNavItem({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }: { isActive: boolean }) =>
        `px-4 py-2 rounded-full text-sm font-medium font-mono transition-colors ${
          isActive
            ? 'bg-accent/25 text-accent ring-1 ring-accent/40'
            : 'text-text-secondary hover:text-text-primary'
        }`
      }
    >
      {children}
    </NavLink>
  );
}
